// --- Dependencies -------------------------------------------------------------------------------
import { Checkbox, MenuItem, Select } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyOption = { value: string; label: string };

type Props = {
    value: string[];
    onChange: (value: string[]) => void;
    options: TyOption[];
    /** Shown while nothing is ticked — "any status", "every branch". */
    anyLabel: string;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Several narrowing choices at once, for the filters a counter actually combines: "being made" and
 * "ready" together, or two of three branches.
 *
 * The closed field names the choice while there is one of it and counts them after that — three
 * status names do not fit a 200px pill, and a row of chips would make the bar taller than the
 * controls beside it.
 */
export default function FilterMultiSelectInput({ value, onChange, options, anyLabel }: Props) {
    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const next = event.target.value;
        onChange(typeof next === "string" ? next.split(",") : next);
    };

    return (
        <Select
            className="filter-multi"
            data-on={value.length > 0}
            multiple
            value={value}
            onChange={handleChange}
            displayEmpty
            size="small"
            renderValue={(picked) => {
                if (picked.length === 0) return <span className="filter-multi-placeholder">{anyLabel}</span>;
                if (picked.length === 1) return options.find((option) => option.value === picked[0])?.label ?? picked[0];
                return `${anyLabel} · ${picked.length}`;
            }}
            MenuProps={{ slotProps: { paper: { className: "filter-select-paper" } } }}
        >
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value} className="filter-multi-item">
                    <Checkbox className="filter-multi-box" size="small" checked={value.includes(option.value)} />
                    <span>{option.label}</span>
                </MenuItem>
            ))}
        </Select>
    );
}
